/**
 * Credits Service
 * 
 * Handles all credit-related API calls including:
 * - Getting credit balance
 * - Getting transaction history
 * - Fetching credit packs
 * - Creating credit pack checkout sessions
 */

import { API_CONFIG, API_ENDPOINTS, CREDIT_COSTS } from '../utils/constants';
import { cacheManager, CACHE_KEYS, CACHE_TTL } from './cacheManager';
import type { User, VideoDuration } from '../types/api';

/**
 * Resolve the credit cost for a given video duration.
 *
 * Server is authoritative — when the user's bootstrap payload includes
 * `credit_costs`, those values win. Falls back to typed constants for
 * older cached SPA bundles or pre-login state. See
 * docs/duration-toggle-plan.md (Decision #2).
 */
export function getCostForDuration(
  duration: VideoDuration,
  user?: Pick<User, 'credit_costs'> | null
): number {
  const serverCosts = user?.credit_costs;
  if (duration === 10) {
    return serverCosts?.video_10s ?? CREDIT_COSTS.VIDEO_GENERATION_10S;
  }
  return serverCosts?.video_5s ?? CREDIT_COSTS.VIDEO_GENERATION;
}

export interface CreditBalanceResponse {
  credits: number;
  user_id: string;
}

export interface Transaction {
  id: string;
  transaction_type: string;
  amount: number;
  balance_after: number;
  operation_type?: string;
  notes?: string;
  created_at: string;
}

export interface TransactionHistoryResponse {
  transactions: Transaction[];
  total: number;
}

export interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price_usd: number;
  description: string;
}

export interface CreditPacksResponse {
  packs: CreditPack[];
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  credits: number;
  price_monthly_usd: number;
  price_annual_usd: number;
  stripe_price_id_monthly: string;
  stripe_price_id_annual: string;
}

export interface SubscriptionPlansResponse {
  plans: SubscriptionPlan[];
}

export interface CreateCreditCheckoutRequest {
  pack_id: 'basic' | 'pro' | 'premium' | 'vip';
  email: string;
  user_id: string;
}

export interface CreateCreditCheckoutResponse {
  url: string;
  session_id: string;
}

export interface CreateCreditSetupIntentRequest {
  pack_id: 'basic' | 'pro' | 'premium' | 'vip';
  email: string;
  user_id: string;
}

export interface CreateCreditSetupIntentResponse {
  success: boolean;
  client_secret: string;
  customer_id: string;
  publishable_key?: string; // Optional: publishable key from backend
}

export interface CreditCheckoutSession {
  id: string;
  payment_status: string;
  credits_granted?: number;
}

export interface CreditCheckoutResponse {
  session: CreditCheckoutSession;
}

/**
 * Get current credit balance for a user
 */
export async function getCreditBalance(userId: string): Promise<CreditBalanceResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CREDITS.BALANCE}?user_id=${userId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch credit balance' }));
    throw new Error(error.error || error.message || 'Failed to fetch credit balance');
  }

  return await response.json();
}

/**
 * Get transaction history for a user
 */
export async function getTransactionHistory(
  userId: string,
  limit: number = 50
): Promise<TransactionHistoryResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CREDITS.HISTORY}?user_id=${userId}&limit=${limit}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch transaction history' }));
    throw new Error(error.error || error.message || 'Failed to fetch transaction history');
  }

  return await response.json();
}

/**
 * Get available credit packs (with caching)
 */
export async function getCreditPacks(): Promise<CreditPacksResponse> {
  // Check cache first
  const cached = cacheManager.get<CreditPacksResponse>(CACHE_KEYS.CREDIT_PACKS);
  if (cached) {
    // Refresh in background
    fetchCreditPacksFromAPI().then(data => {
      cacheManager.set(CACHE_KEYS.CREDIT_PACKS, data, CACHE_TTL.CREDIT_PACKS);
    }).catch(() => {
      // Silent fail for background refresh
    });
    
    return cached;
  }

  // Cache miss - fetch and store
  const data = await fetchCreditPacksFromAPI();
  cacheManager.set(CACHE_KEYS.CREDIT_PACKS, data, CACHE_TTL.CREDIT_PACKS);
  return data;
}

/**
 * Internal function to fetch credit packs from API
 */
async function fetchCreditPacksFromAPI(): Promise<CreditPacksResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.CREDIT_PACKS}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch credit packs' }));
    throw new Error(error.error || error.message || 'Failed to fetch credit packs');
  }

  return await response.json();
}

/**
 * Get subscription plans with Stripe prices (with caching)
 */
export async function getSubscriptionPlans(): Promise<SubscriptionPlansResponse> {
  // Check cache first
  const cached = cacheManager.get<SubscriptionPlansResponse>(CACHE_KEYS.SUBSCRIPTION_PLANS);
  if (cached) {
    // Refresh in background
    fetchSubscriptionPlansFromAPI().then(data => {
      cacheManager.set(CACHE_KEYS.SUBSCRIPTION_PLANS, data, CACHE_TTL.SUBSCRIPTION_PLANS);
    }).catch(() => {
      // Silent fail for background refresh
    });
    
    return cached;
  }

  // Cache miss - fetch and store
  const data = await fetchSubscriptionPlansFromAPI();
  cacheManager.set(CACHE_KEYS.SUBSCRIPTION_PLANS, data, CACHE_TTL.SUBSCRIPTION_PLANS);
  return data;
}

/**
 * Internal function to fetch subscription plans from API
 */
async function fetchSubscriptionPlansFromAPI(): Promise<SubscriptionPlansResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.SUBSCRIPTION_PLANS}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch subscription plans' }));
    throw new Error(error.error || error.message || 'Failed to fetch subscription plans');
  }

  return await response.json();
}

/**
 * Create a credit pack checkout session (legacy - redirects to Stripe)
 */
export async function createCreditCheckout(
  request: CreateCreditCheckoutRequest
): Promise<CreateCreditCheckoutResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.CREATE_CREDIT_CHECKOUT}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create checkout session' }));
    throw new Error(error.error || error.message || 'Failed to create checkout session');
  }

  return await response.json();
}

/**
 * Create a setup intent for embedded credit pack checkout
 */
export async function createCreditSetupIntent(
  request: CreateCreditSetupIntentRequest
): Promise<CreateCreditSetupIntentResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/stripe/create-credit-setup-intent`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create setup intent' }));
    throw new Error(error.error || error.message || 'Failed to create setup intent');
  }

  return await response.json();
}

/**
 * Complete credit pack purchase after payment method is collected
 */
export async function completeCreditPurchase(
  customerId: string,
  paymentMethodId: string,
  packId: 'basic' | 'pro' | 'premium' | 'vip',
  userId: string
): Promise<{ success: boolean; credits_granted?: number; payment_intent_id?: string }> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/stripe/complete-credit-purchase`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customerId,
        payment_method_id: paymentMethodId,
        pack_id: packId,
        user_id: userId,
      }),
    }
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Failed to complete purchase' }));
    // Attach Stripe-structured fields onto the Error so callers can capture
    // them in analytics (PostHog payment_failed event). API exposes:
    //   - 402 card_error: { error: 'card_error', code, decline_code, message, payment_intent_id }
    //   - 502 stripe_error: { error: 'stripe_error', code, message }
    //   - 500 server_error: { error: <str>, error_type: 'server_error' }
    const message = body.message || body.error || 'Failed to complete purchase';
    const err = new Error(message) as Error & {
      status?: number;
      error_code?: string | null;
      decline_code?: string | null;
      error_type?: string | null;
      payment_intent_id?: string | null;
    };
    err.status = response.status;
    err.error_code = body.code ?? null;
    err.decline_code = body.decline_code ?? null;
    err.error_type = body.error || body.error_type || null;
    err.payment_intent_id = body.payment_intent_id ?? null;
    throw err;
  }

  return await response.json();
}

/**
 * Get credit checkout session status
 */
export async function getCreditCheckoutSession(
  sessionId: string
): Promise<CreditCheckoutResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.CREDIT_CHECKOUT}/${sessionId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch checkout session' }));
    throw new Error(error.error || error.message || 'Failed to fetch checkout session');
  }

  return await response.json();
}

/**
 * Sign up authenticated user (grants 100 credits)
 */
export async function signupUser(
  email: string,
  supabaseUserId: string,
  fullName?: string
): Promise<{ user: any; signup_bonus_granted: boolean; credits: number; grant_status?: 'granted' | 'already_granted' | 'failed' }> {
  console.log('📝 [signupUser] Calling POST /api/user with:', {
    email,
    supabaseUserId,
    fullName,
    endpoint: `${API_CONFIG.BASE_URL}${API_ENDPOINTS.USER.SIGNUP}`
  });

  try {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}${API_ENDPOINTS.USER.SIGNUP}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          supabase_user_id: supabaseUserId,
          full_name: fullName,
        }),
      }
    );

    console.log('📝 [signupUser] Response status:', response.status);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to sign up user' }));
      console.error('❌ [signupUser] API error:', error);
      throw new Error(error.error || error.message || 'Failed to sign up user');
    }

    const result = await response.json();
    console.log('✅ [signupUser] Success:', {
      signup_bonus_granted: result.signup_bonus_granted,
      credits: result.credits
    });
    
    return result;
  } catch (error) {
    console.error('❌ [signupUser] Exception:', error);
    throw error;
  }
}

/**
 * pack_type values supported by the standalone credit-pack endpoints.
 * 'standalone'    → V1 credit_pack variant catalog
 * 'standalone_v2' → V2 credit_pack_v2 variant catalog (A/B/C test, 25%)
 */
export type StandalonePackType = 'standalone' | 'standalone_v2';

/**
 * Get standalone credit packs. Defaults to V1 catalog; pass 'standalone_v2'
 * to fetch the V2 catalog for the credit_pack_v2 variant.
 */
export async function getStandaloneCreditPacks(
  packType: StandalonePackType = 'standalone'
): Promise<CreditPacksResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.CREDIT_PACKS}?type=${packType}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch credit packs' }));
    throw new Error(error.error || error.message || 'Failed to fetch credit packs');
  }

  return await response.json();
}

/**
 * Create a Stripe checkout session for standalone credit pack purchase.
 * Redirects user to Stripe Checkout (payment mode, one-time).
 */
export async function createStandaloneCreditCheckout(
  request: { pack_id: string; email: string; user_id: string },
  packType: StandalonePackType = 'standalone'
): Promise<{ success: boolean; url: string; session_id: string; credits: number; price: number }> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.CREATE_CREDIT_CHECKOUT}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...request,
        pack_type: packType,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create checkout' }));
    throw new Error(error.error || error.message || 'Failed to create checkout');
  }

  return await response.json();
}

/**
 * Create a setup intent for standalone credit pack embedded checkout.
 * Same as createCreditSetupIntent but passes a standalone pack_type
 * to bypass subscription validation.
 */
export async function createStandaloneCreditSetupIntent(
  request: { pack_id: string; email: string; user_id: string },
  packType: StandalonePackType = 'standalone'
): Promise<CreateCreditSetupIntentResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/stripe/create-credit-setup-intent`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...request,
        pack_type: packType,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create setup intent' }));
    throw new Error(error.error || error.message || 'Failed to create setup intent');
  }

  return await response.json();
}

/**
 * Complete standalone credit pack purchase after payment method is collected.
 */
export async function completeStandaloneCreditPurchase(
  customerId: string,
  paymentMethodId: string,
  packId: string,
  userId: string,
  packType: StandalonePackType = 'standalone'
): Promise<{ success: boolean; credits_granted?: number; new_balance?: number; payment_intent_id?: string }> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/stripe/complete-credit-purchase`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customerId,
        payment_method_id: paymentMethodId,
        pack_id: packId,
        pack_type: packType,
        user_id: userId,
      }),
    }
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Failed to complete purchase' }));
    // See completeCreditPurchase above — attach structured Stripe error fields
    // so PostHog payment_failed can segment by decline_code.
    const message = body.message || body.error || 'Failed to complete purchase';
    const err = new Error(message) as Error & {
      status?: number;
      error_code?: string | null;
      decline_code?: string | null;
      error_type?: string | null;
      payment_intent_id?: string | null;
    };
    err.status = response.status;
    err.error_code = body.code ?? null;
    err.decline_code = body.decline_code ?? null;
    err.error_type = body.error || body.error_type || null;
    err.payment_intent_id = body.payment_intent_id ?? null;
    throw err;
  }

  return await response.json();
}

/**
 * Set purchase_type for a user (A/B test variant assignment).
 * Called once per user on first /upgrade visit.
 * Atomic: only sets if currently NULL.
 */
export async function setPurchaseType(
  userId: string,
  purchaseType: 'subscription' | 'credit_pack' | 'credit_pack_v2' | 'credit_pack_hybrid' | 'credit_pack_premium'
): Promise<{ success: boolean; purchase_type: string }> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/user/set-purchase-type`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        purchase_type: purchaseType,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to set purchase type' }));
    throw new Error(error.error || error.message || 'Failed to set purchase type');
  }

  return await response.json();
}

// ─── Saved-card one-click checkout ────────────────────────────────────────────

export interface SavedCardResponse {
  has_default_pm: boolean;
  feature_enabled: boolean;
  brand?: string;
  last4?: string;
}

/**
 * Look up the user's saved default payment method (display data only).
 * Returns has_default_pm: false when:
 *   - SAVED_CARD_CHECKOUT_ENABLED is off (feature_enabled: false)
 *   - User has no Stripe customer / no default PM
 *   - Default PM is non-card (e.g. bank account) — quick-buy is card-only for v1
 *   - Any error during lookup (fail closed to existing checkout flow)
 */
export async function getSavedCard(userId: string): Promise<SavedCardResponse> {
  try {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.SAVED_CARD}?user_id=${encodeURIComponent(userId)}`,
      { method: 'GET' }
    );
    if (!response.ok) {
      return { has_default_pm: false, feature_enabled: true };
    }
    return await response.json();
  } catch {
    // Network error — fall back to embedded checkout, never block purchase
    return { has_default_pm: false, feature_enabled: true };
  }
}

export interface QuickBuyResponse {
  success: boolean;
  requires_action?: boolean;
  client_secret?: string;
  payment_intent_id?: string;
  credits_granted?: number;
  new_balance?: number;
  // Error fields (HTTP non-2xx)
  error?: string;
  code?: string;
  decline_code?: string;
  message?: string;
}

/**
 * One-click credit pack purchase using the customer's default PM.
 *
 * Outcomes:
 *   { success: true, ... }                        -> credits granted
 *   { success: false, requires_action: true, ... } -> caller must run 3DS via
 *                                                     stripe.confirmCardPayment(client_secret),
 *                                                     then call this again to finalize
 *   thrown 409 with error 'no_saved_card'         -> caller should fall back to
 *                                                     embedded SetupIntent flow
 *   thrown 409 with error 'feature_disabled'      -> kill switch, fall back
 *   thrown 402 with code/decline_code             -> card decline, branch UX on hardness
 *   thrown 5xx                                     -> generic error
 *
 * Errors carry the parsed JSON body on `(err as any).body` so callers can branch.
 */
export async function quickBuyCreditPack(
  userId: string,
  packId: string,
  packType: 'standalone' | 'standalone_v2' | 'addon'
): Promise<QuickBuyResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.QUICK_BUY_CREDIT_PACK}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        pack_id: packId,
        pack_type: packType,
      }),
    }
  );

  const data: QuickBuyResponse = await response.json().catch(() => ({
    success: false,
    error: 'parse_error',
    message: 'Failed to parse response',
  } as QuickBuyResponse));

  if (!response.ok) {
    const err = new Error(data.message || data.error || 'Quick-buy failed') as Error & {
      status: number;
      body: QuickBuyResponse;
    };
    err.status = response.status;
    err.body = data;
    throw err;
  }

  return data;
}

export interface WalletPurchaseResponse {
  success: boolean;
  requires_action?: boolean;
  client_secret?: string;
  payment_intent_id?: string;
  credits_granted?: number;
  new_balance?: number;
  wallet_type?: string;
  // Error fields
  error?: string;
  code?: string;
  decline_code?: string;
  message?: string;
}

/**
 * Wallet credit pack purchase (Apple Pay / Google Pay / Link).
 *
 * Called from WalletCheckoutButton after ExpressCheckoutElement returns a
 * payment_method_id from the wallet sheet. The backend creates + confirms a
 * PaymentIntent against this PM and grants credits inline on success.
 *
 * Outcomes mirror quickBuyCreditPack:
 *   { success: true, ... }                          -> credits granted
 *   { success: false, requires_action: true, ... }  -> caller runs 3DS via
 *                                                       stripe.confirmPayment(client_secret),
 *                                                       then calls this again
 *   thrown 402 with code/decline_code               -> card decline
 *   thrown 4xx/5xx                                  -> generic / validation error
 *
 * Errors carry the parsed JSON body on `(err as any).body` so callers can branch.
 */
export async function walletPurchase(
  userId: string,
  paymentMethodId: string,
  packId: string,
  packType: 'standalone' | 'standalone_v2' | 'addon',
  walletType: 'apple_pay' | 'google_pay' | 'link' | null,
  // On retry after a 3DS challenge, the caller passes the existing PI id so
  // the server can retrieve its live status instead of re-calling
  // PaymentIntent.create (which would replay the cached requires_action
  // response from Stripe's idempotency cache).
  existingPaymentIntentId?: string | null
): Promise<WalletPurchaseResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.WALLET_PURCHASE}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        payment_method_id: paymentMethodId,
        pack_id: packId,
        pack_type: packType,
        wallet_type: walletType || undefined,
        payment_intent_id: existingPaymentIntentId || undefined,
      }),
    }
  );

  const data: WalletPurchaseResponse = await response.json().catch(() => ({
    success: false,
    error: 'parse_error',
    message: 'Failed to parse response',
  } as WalletPurchaseResponse));

  if (!response.ok) {
    const err = new Error(data.message || data.error || 'Wallet purchase failed') as Error & {
      status: number;
      body: WalletPurchaseResponse;
    };
    err.status = response.status;
    err.body = data;
    throw err;
  }

  return data;
}

export interface RetargetingBonusResponse {
  success: boolean;
  new_balance?: number;
  transaction_id?: string;
  reason?: string;
  message?: string;
}

/**
 * Claim retargeting bonus (100 credits) for eligible users.
 * Eligible if users.total_credits_used < 200.
 */
export async function claimRetargetingBonus(
  userId: string
): Promise<RetargetingBonusResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CREDITS.RETARGETING_BONUS}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_id: userId }),
    }
  );

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      reason: data.reason || 'error',
      message: data.message || data.error || 'Failed to claim bonus',
    };
  }

  return data;
}

